import React, { useEffect, useState } from "react";
import DashboardLayout from "../components/dashboard/DashboardLayout";
import api from "../lib/api";
import {
  Gift,
  Copy,
  Check,
  Users,
  TrendingUp,
  Trophy,
  DollarSign,
  Share2,
} from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="bb-card rounded-2xl p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-white/50 text-[12px] mb-2">
      <Icon size={15} className={accent} />
      {label}
    </div>
    <div className="font-display font-bold text-[24px] text-white">{value}</div>
  </div>
);

const LEVELS = [
  { key: "l1", label: "Level 1 — Direct", rate: "10%", color: "text-emerald-400" },
  { key: "l2", label: "Level 2", rate: "5%", color: "text-sky-400" },
  { key: "l3", label: "Level 3", rate: "3%", color: "text-violet-400" },
];

const Affiliate = () => {
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState("l1");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [me, lb] = await Promise.all([
          api.get("/affiliate/me"),
          api.get("/affiliate/leaderboard"),
        ]);
        setData(me.data);
        setBoard(lb.data);
      } catch (e) { /* noop */ }
      finally { setLoading(false); }
    })();
  }, []);

  const copyLink = () => {
    if (!data?.referral_link) return;
    navigator.clipboard.writeText(data.referral_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const progress = data ? Math.min(100, (data.month_earned / data.goal) * 100) : 0;
  const totalRefs = data ? data.counts.l1 + data.counts.l2 + data.counts.l3 : 0;

  return (
    <DashboardLayout title="Affiliate Program" subtitle="Earn" activeKey="affiliate">
      <div className="space-y-6" data-testid="affiliate-page">
        {/* Hero / referral link */}
        <div className="bb-card rounded-3xl p-6 lg:p-8 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-56 h-56 bg-[#1c3df5]/20 blur-3xl rounded-full pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1c3df5]/15 border border-[#1c3df5]/30 text-[11px] text-[#9db0ff] font-medium mb-4">
              <Gift size={13} /> Earn up to 18% across 3 levels
            </div>
            <h2 className="font-display font-bold text-[24px] lg:text-[28px] text-white mb-1">
              Invite traders. Earn <span className="text-[#7d8eff]">$1,000+/month</span>.
            </h2>
            <p className="text-[13px] text-white/55 mb-5 max-w-xl">
              Share your link. Earn <b className="text-white/80">10%</b> on direct referrals,
              <b className="text-white/80"> 5%</b> on their referrals, and <b className="text-white/80">3%</b> on the
              third level — on <b className="text-white/80">every deposit of $20+</b>. Paid instantly to your balance.
            </p>

            <label className="text-[11px] uppercase tracking-wider text-white/40">Your referral link</label>
            <div className="mt-1.5 flex flex-col sm:flex-row gap-2">
              <div className="flex-1 h-11 px-4 flex items-center bg-black/30 border border-white/10 rounded-xl text-[13px] text-white/80 truncate" data-testid="affiliate-link">
                {loading ? "Loading…" : data?.referral_link}
              </div>
              <button
                onClick={copyLink}
                data-testid="affiliate-copy-btn"
                className="h-11 px-5 rounded-xl bb-blue text-white text-[13px] font-medium inline-flex items-center justify-center gap-2 shrink-0"
              >
                {copied ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy link</>}
              </button>
            </div>
            <div className="mt-2 text-[12px] text-white/45">
              Referral code: <span className="text-white/80 font-mono tracking-wider">{data?.referral_code || "—"}</span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={DollarSign} label="Total earned" value={fmt(data?.total_earned)} accent="text-emerald-400" testid="stat-total-earned" />
          <StatCard icon={TrendingUp} label="This month" value={fmt(data?.month_earned)} accent="text-sky-400" testid="stat-month-earned" />
          <StatCard icon={Users} label="Total referrals" value={totalRefs} accent="text-violet-400" testid="stat-total-refs" />
          <StatCard icon={Gift} label="Direct (L1)" value={data?.counts?.l1 ?? 0} accent="text-[#7d8eff]" testid="stat-l1" />
        </div>

        {/* $1000 progress */}
        <div className="bb-card rounded-2xl p-6" data-testid="affiliate-goal">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-white text-[14px] font-medium">
              <Trophy size={16} className="text-amber-400" /> $1,000/month goal
            </div>
            <div className="text-[13px] text-white/60">{fmt(data?.month_earned)} / {fmt(data?.goal)}</div>
          </div>
          <div className="h-3 rounded-full bg-white/[0.06] overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#1c3df5] to-[#7d8eff] rounded-full transition-all duration-700" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-[12px] text-white/45 mt-2">
            {progress >= 100 ? "🎉 Goal reached! Keep stacking." : "Tip: ~20 active referrals depositing $500/mo gets you to $1,000."}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Downline */}
          <div className="lg:col-span-2 bb-card rounded-2xl p-5" data-testid="affiliate-network">
            <div className="flex items-center gap-2 mb-4">
              {LEVELS.map((l) => (
                <button
                  key={l.key}
                  onClick={() => setTab(l.key)}
                  className={`px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors ${
                    tab === l.key ? "bg-[#1c3df5]/20 text-white border border-[#1c3df5]/30" : "text-white/50 hover:text-white border border-transparent"
                  }`}
                  data-testid={`network-tab-${l.key}`}
                >
                  {l.label.split(" — ")[0]} <span className={l.color}>{l.rate}</span>
                </button>
              ))}
            </div>
            {(data?.downline?.[tab] || []).length === 0 ? (
              <div className="py-10 text-center text-white/40 text-[13px]">
                No referrals at this level yet. Share your link to start earning.
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {(data?.downline?.[tab] || []).map((u, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5">
                    <div className="min-w-0">
                      <div className="text-[13px] text-white truncate">{u.name}</div>
                      <div className="text-[11px] text-white/40 truncate">{u.email}</div>
                    </div>
                    <span className={`text-[11px] px-2 py-0.5 rounded ${u.verified ? "bg-emerald-500/15 text-emerald-300" : "bg-white/[0.05] text-white/40"}`}>
                      {u.verified ? "Verified" : "Pending"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Leaderboard */}
          <div className="bb-card rounded-2xl p-5" data-testid="affiliate-leaderboard">
            <div className="flex items-center gap-2 text-white text-[14px] font-medium mb-4">
              <Trophy size={16} className="text-amber-400" /> Top affiliates
            </div>
            {board.length === 0 ? (
              <div className="py-8 text-center text-white/40 text-[13px]">Be the first on the board!</div>
            ) : (
              <div className="space-y-2">
                {board.map((r) => (
                  <div key={r.rank} className="flex items-center gap-3 py-1.5">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                      r.rank === 1 ? "bg-amber-400/20 text-amber-300" : r.rank <= 3 ? "bg-white/10 text-white/80" : "bg-white/[0.04] text-white/50"
                    }`}>{r.rank}</span>
                    <span className="flex-1 text-[13px] text-white/85 truncate">{r.name}</span>
                    <span className="text-[13px] font-semibold text-emerald-300">{fmt(r.earned)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent commissions */}
        <div className="bb-card rounded-2xl p-5" data-testid="affiliate-commissions">
          <div className="flex items-center gap-2 text-white text-[14px] font-medium mb-4">
            <Share2 size={16} className="text-[#7d8eff]" /> Recent commissions
          </div>
          {(data?.recent_commissions || []).length === 0 ? (
            <div className="py-8 text-center text-white/40 text-[13px]">No commissions yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-white/40 text-[11px] uppercase tracking-wider text-left">
                    <th className="py-2 font-medium">From</th>
                    <th className="py-2 font-medium">Level</th>
                    <th className="py-2 font-medium">Deposit</th>
                    <th className="py-2 font-medium text-right">Earned</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {data.recent_commissions.map((c, i) => (
                    <tr key={i}>
                      <td className="py-2.5 text-white/85">{c.from_user_name}</td>
                      <td className="py-2.5 text-white/60">L{c.level} ({Math.round(c.pct * 100)}%)</td>
                      <td className="py-2.5 text-white/60">{fmt(c.deposit_amount)}</td>
                      <td className="py-2.5 text-right font-semibold text-emerald-300">{fmt(c.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Affiliate;
